import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';
import { Input, Label } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withError, withValidation } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { WebpageCondition, DsaTarget } from '@bingads-webui/campaign-odata-schemas';
import { DSA_TARGET_OPERAND_TYPES, DSA_TARGET_OPERATOR_TYPES, generateKeyAndFormateDsaTarget } from '@bingads-webui-campaign-react/search-campaign-data';
import { ValidatedCategorySelector } from './category-selector';
import { useCurrencyCode } from '../../utils';
import { getCategoryTargetItemStyles } from './styles/category-target-item-style';

const ValidatedTextInput = withControlErrorHandling(withUnvalidatedControlErrors(withError(withValidation(Input))));

export const CategoryTargetItem = ({
  preloadedCategories,
  domainName,
  languageCode,
  disabledCategories,
  item,
  onItemChange,
  alwaysShowError,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCategoryTargetItemStyles);
  const currencyCode = useCurrencyCode();

  const { conditions } = item || {};
  const condition = _.first(conditions) || {};

  const onValidateArgumentOperand = useCallback((res) => {
    if (item) {
      const { key, conditions: newConditions } = generateKeyAndFormateDsaTarget([{ ...res.data, Operator: DSA_TARGET_OPERATOR_TYPES.Equals, Operand: DSA_TARGET_OPERAND_TYPES.Category }]);
      onItemChange({
        ...item,
        key,
        conditions: newConditions,
        isValid: res.isValid,
      });
    }
  }, [item, onItemChange]);

  const onValidateBid = useCallback((res) => {
    if (item) {
      onItemChange({
        ...item,
        Bid: res.data.Bid,
        isValid: res.isValid,
      });
    }
  }, [item, onItemChange]);

  return (
    <div className={classnames(classes.customLabelEditRowContainer, 'custom-label-edit-row-container')}>
      <div className={classnames(classes.editRow, 'category-edit-row')}>
        <ValidatedCategorySelector
          validationProps={{
            data: condition,
            onValidate: onValidateArgumentOperand,
            field: 'ArgumentOperand',
            schema: WebpageCondition,
            alwaysShowError,
          }}
          disabledCategories={disabledCategories}
          preloadedCategories={preloadedCategories}
          domainName={domainName}
          languageCode={languageCode}
          i18n={i18n}
        />
        <div className={classnames(classes.bidEdit, 'column-bid-edit')}>
          <ValidatedTextInput
            validationProps={{
              data: item,
              onValidate: onValidateBid,
              field: 'Bid',
              schema: DsaTarget,
            }}
            i18n={i18n}
          />
        </div>
        <Label>{currencyCode}</Label>
      </div>
    </div>
  );
};

CategoryTargetItem.propTypes = {
  disabledCategories: PropTypes.shape({}).isRequired,
  item: PropTypes.shape({}).isRequired,
  onItemChange: PropTypes.func.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})),
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  alwaysShowError: PropTypes.bool.isRequired,
};

CategoryTargetItem.defaultProps = {
  preloadedCategories: null,
};
